import { RefObject } from 'preact';
import { ViewportConfig } from '../../hooks/UNSAFE_useViewportIntersect';
import { SelectionRangeDetail } from '../../hooks/PRIVATE_useSelection';
import { ListItemRendererContext } from '../../UNSAFE_Collection';
import { isKeyDefined } from '../UNSAFE_keys';
import { getClientHints } from '../PRIVATE_clientHints';
import { FocusableElement } from '../PRIVATE_tabbableUtils';

/**
 * Returns the key from the item element
 */
export const getKey = (item: HTMLElement) => {
  return item.dataset['ojKeyType'] === 'number'
    ? Number(item.dataset['ojKey'])
    : item.dataset['ojKey'];
};

/**
 * A helper function that return the key of the first available item.
 */
export const getFirstVisibleKey = (root: HTMLElement, itemSelector: string) => {
  if (root) {
    const firstItem = root.querySelector(itemSelector);
    if (firstItem) {
      const key = getKey(firstItem as HTMLElement);
      return key;
    }
  }
  return null;
};

/**
 * A helper function to find the element with the specified key.
 * Returns null if the element with key is not found.
 */
export const findElementByKey = <K extends string | number>(
  root: HTMLElement,
  key: K,
  itemSelector: string
) => {
  if (root) {
    const items = root.querySelectorAll(itemSelector);
    for (let i = 0; i < items.length; i++) {
      const thisKey = getKey(items[i] as HTMLElement);
      if (key == (thisKey as K)) {
        return items[i];
      }
    }
  }
  return null;
};

/**
 * A helper function that returns the key of the previous or the next item
 * given the specified current key by traversing indices instead of sibling elements.
 */
export const getPrevNextKey = <K extends string | number>(
  root: HTMLElement | null,
  currentKey: K | undefined,
  isPrev: boolean,
  itemSelector: string
) => {
  return (): K | null => {
    let currItem, currIndex;
    if (isKeyDefined(currentKey) && root) {
      const items = root.querySelectorAll(itemSelector);
      for (let i = 0; i < items.length; i++) {
        if (getKey(items[i] as HTMLElement) === currentKey) {
          currItem = items[i];
          currIndex = i;
          break;
        }
      }
      if (currItem) {
        currIndex = currIndex ? currIndex : 0;
        const nextItem = isPrev ? items[currIndex - 1] : items[currIndex + 1];
        if (nextItem) {
          const key = getKey(nextItem as HTMLElement) as K;
          if (key != null) {
            return key;
          }
        }
        // no next item or next item is not a valid item
        return null;
      }
      const firstItem = items[0] as HTMLElement;
      return getKey(firstItem) as K;
    }
    return null;
  };
};

/**
 * A helper wrapper function so we use ref as root instead of a HTMLElement for prev/next currentKey behavior.
 * Used in comps that have a initialSelection due to this bug: JET-59240
 */
export const getPrevNextKeyUsingRef = <K extends string | number>(
  root: RefObject<HTMLElement>,
  currentKey: K | undefined,
  isPrev: boolean,
  itemSelector: string
) => {
  return (): K | null => {
    return getPrevNextKey(root.current, currentKey, isPrev, itemSelector)();
  };
};

/**
 * A helper function that returns the key of the previous or the next item by count,
 * given the specified current key
 */
export const getPrevNextKeyByCount = <K, D>(
  data: D[] | null,
  getRowKey: (data: D) => K,
  currentKey?: K,
  count = 0
) => {
  if (data && currentKey != undefined) {
    const currIndex = data.findIndex((d) => {
      return getRowKey(d) === currentKey;
    });
    const nextIndex = currIndex + count;
    if (0 <= nextIndex && nextIndex < data.length) {
      return getRowKey(data[nextIndex]);
    }
    return null;
  }
  return null;
};

/**
 * A helper function to get the key from an element
 */
export const keyExtractor = <K extends string | number>(
  element: HTMLElement,
  itemSelector: string
): K | null => {
  const item = element.closest(itemSelector);
  if (item) {
    const key = getKey(item as HTMLElement) as K;
    return key === undefined ? null : key;
  }
  return null;
};

/**
 * If excludeSelf is true, returns a function that determines if a given element is a descendent
 * of the root element. Otherwise, returns a function that determines if a given element is the
 * root element or is a descendant of the root element.
 */
export const getElementContainsFunc = (root?: HTMLElement | null, excludeSelf?: boolean) => {
  return (elem: Element | null) => {
    if (root != null) {
      return excludeSelf ? root !== elem && root.contains(elem) : root.contains(elem);
    }
    return false;
  };
};

/**
 * A helper function to that return viewPortConfig to collection components
 */
export const getViewportConfig = (rootRef: RefObject<HTMLElement>, config?: ViewportConfig) => {
  return (
    config ?? {
      scroller: () => {
        return rootRef.current;
      }
    }
  );
};

/**
 * A helper function to handle range selection
 */
export const handleSelectionRange = <K, D>(
  detail: SelectionRangeDetail<K>,
  data: D[],
  getRowKey: (data: D) => K
) => {
  const keys = data.map((value: D) => {
    return getRowKey(value);
  });
  const startIndex = keys.indexOf(detail.value.start);
  const endIndex = keys.indexOf(detail.value.end);
  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  if (minIndex === -1) {
    // return a range to fetch so we can find all the keys, maxIndex should not be -1
    return { offset: 0, count: -1 };
  } else {
    // range of keys are in the current viewport, return them
    return keys.slice(minIndex, maxIndex + 1);
  }
};

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if running on a mobile device, false otherwise
 */
export function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}

/**
 * Handle setting focus where required when entering actionable mode, or exiting actionable mode if
 * no tabbable children are found.
 */
export function handleEnterActionableMode<K>(
  hasFocusWithin: boolean,
  tabbableElements: FocusableElement[],
  setCurrentTabbableKey: (key?: K) => void
) {
  if (!hasFocusWithin) {
    const tabbableCount = tabbableElements.length;
    if (tabbableCount > 0) {
      tabbableElements[0].focus();
    } else {
      // exit actionable mode as there are no tabbable children
      setCurrentTabbableKey(undefined);
    }
  }
}

/**
 * Handle wrapping focus to the correct tabbable child when either trap edge is focused. Will also
 * exit actionable mode if no tabbable children are found, though that should not happen in practice.
 */
export function handleWrapActionableFocus<K>(
  isStart: boolean,
  tabbableElements: FocusableElement[],
  setCurrentTabbableKey: (key?: K) => void
) {
  const tabbableCount = tabbableElements.length;
  if (tabbableCount > 0) {
    if (isStart) {
      tabbableElements[tabbableCount - 1].focus();
    } else {
      tabbableElements[0].focus();
    }
  } else {
    // exit actionable mode as there are no tabbable children
    setCurrentTabbableKey(undefined);
  }
}

/**
 * Compare the equality between two ListItemRendererContexts, especially useful for memoized items
 */
export const compareListItemContext = <K, D>(
  ctx1: ListItemRendererContext<K, D>,
  ctx2: ListItemRendererContext<K, D>
) => {
  return (
    ctx1.data === ctx2.data &&
    ctx1.index === ctx2.index &&
    ctx1.isSelected === ctx2.isSelected &&
    ctx1.metadata.key === ctx2.metadata.key
  );
};
